import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { REPORT_CONSTANTS } from "~/helpers/constants";
import { getReportStatus } from "~/helpers/reportStatus.helper";
import type { ReportData } from "~/types/global";
import { useReportCRUD } from "~/hooks/useReportCRUD";
import { useUserCRUD } from "~/hooks/useUserCRUD";
import { useUserData } from "~/store/userStore";
import { createErrorHandler } from "~/helpers/errorHandling.helper";
import { AUTH_STORE_INSTANCE } from "~/store/auth";
import { usePasswordCheck } from "~/hooks/usePasswordCheck";
import { REPORT_DETAIL_STORE_INSTANCE } from "~/store/details";
import { useReportDetailStore } from "~/store/details/store";
import { useReportListActions, useReportListStore } from "~/store/reportListStore";
import { markSaveIssued, releaseSave, shouldIssueSave } from "~/helpers/reportSaveDedup.helper";
import { recordCreateCompleted } from "~/helpers/perceivedLatency.helper";
import { consumeSummaryDuration } from "~/helpers/aiSummaryLatency.helper";
import { emitEvent } from "~/helpers/telemetry.helper";
import { normalizeErrorMessage } from "_@snap/reports/helpers";
import { useSectionStatusStore } from "~/store/sectionStatusStore";
import { useReportProgressStore } from "~/store/reportProgressStore";
import { parseSectionEvent } from "~/helpers/sectionEvents.helper";
import { SECTION_MESSAGE_TYPE } from "~/types/section-streaming";
import { useSectionPersistence } from "~/hooks/useSectionPersistence";
import { useReportPartialSave } from "~/hooks/useReportPartialSave";
import { parseReportPartialFrame } from "~/helpers/reportPartialFrame.helper";
import { mergeReportDetailsPartialSnapshot } from "~/helpers/reportDetailsPartialCache.helper";
import { claimPartialSequenceForRender } from "~/helpers/reportPartialSaveQueue.helper";
import {
  extractIncrementalSectionPayload,
  extractRsIdFromSectionId,
} from "~/helpers/incrementalSectionMerge.helper";
import { resolveIdentityLabels } from "_@snap/reports/config/redes-sociais-identity-fields.config";
import { useReportStatusSocketStore } from "~/store/reportStatusSocketStore";
import {
  hasExplicitStatusSocketWork,
  hasPendingWorkInSnapshots,
  markStatusSocketWork,
  shouldReconnectStatusSocket,
} from "~/helpers/status-socket-work.helper";

export function PendingReportSockets() {
  const { addNewReportMutation, invalidateCurrentFolderNoFilters, invalidateReportDetails, updateResumeMutation, invalidateSummary } = useReportCRUD(null, false);
  const { invalidateUser: invalidateUserData } = useUserCRUD();
  const userData = useUserData();
  const wsRef = useRef<WebSocket | null>(null);
  // Per-id dedup of the backend's re-sent `completed`, kept across re-renders.
  const issuedReportSavesRef = useRef<Set<string>>(new Set());
  // Same dedup for summaries, keyed by `summary_id` and applied after the
  // stale-generation check below.
  const issuedSummarySavesRef = useRef<Set<string>>(new Set());
  const queryClient = useQueryClient();
  const {
    selectors: { useIsAuthorized },
  } = AUTH_STORE_INSTANCE;
  const isUserAuthorized = useIsAuthorized();
  const { withPasswordCheck } = usePasswordCheck();
  const { setSummaryData, setSummaryStatus, setIsGeneratingSummary, setSummaryErrorMessage } =
    REPORT_DETAIL_STORE_INSTANCE.selectors.useReportDetailActions();
  const { updateItemById } = useReportListActions();

  // Section streaming durability: persist a section on its `completed` event,
  // and restore the current partial after a reconnect.
  const { persistCompletedSection, repullCurrentPartial } = useSectionPersistence();
  // Read by ref so `connect` can call them without depending on their identity.
  const persistCompletedSectionRef = useRef(persistCompletedSection);
  persistCompletedSectionRef.current = persistCompletedSection;
  const repullCurrentPartialRef = useRef(repullCurrentPartial);
  repullCurrentPartialRef.current = repullCurrentPartial;
  // Partial cumulative snapshot save: reuses the report's own DEK, sends
  // structured non-terminal status, never touched by the terminal flow.
  const { savePartialReportMutation, markReportTerminal } = useReportPartialSave();
  // A later open is a reconnect, not the initial connection.
  const hasConnectedRef = useRef(false);
  // Bounded exponential backoff, reset to the base delay on a successful open.
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastAttemptAtRef = useRef(0);
  const reconnectDelayRef = useRef(2000);
  const RECONNECT_BASE_DELAY_MS = 2000;
  const RECONNECT_MAX_DELAY_MS = 30000;
  const WS_POLICY_VIOLATION = 1008;

  const userId = userData?.user_id;
  // Handlers close over connection-time values; the close guard must see current
  // auth, not what it was when the socket opened.
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const isUserAuthorizedRef = useRef(isUserAuthorized);
  isUserAuthorizedRef.current = isUserAuthorized;

  // Cached list/details payloads are the freshest server evidence of pending work.
  const cachedReportsRef = useRef(() => {
    if (typeof queryClient?.getQueriesData !== "function") return [];
    const pages = queryClient.getQueriesData<{ data?: unknown[] }>({
      queryKey: ["reports", "list"],
      exact: false,
    });
    const listItems = pages.flatMap(([, page]) => (Array.isArray(page?.data) ? page.data : []));
    const details = queryClient
      .getQueriesData<unknown>({ queryKey: ["reports", "details"], exact: false })
      .map(([, report]) => report);
    return [...listItems, ...details];
  });

  const connect = useCallback((force = false) => {
    if (!userId || !isUserAuthorized) return;

    // Exactly one live connection. Never close/reopen here — that could drop a
    // terminal message in transit; the frontend only closes on cleanup.
    const existing = wsRef.current;
    if (
      existing &&
      (existing.readyState === WebSocket.OPEN ||
        existing.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    // Passive triggers (evidence in the cache) must never bypass the backoff:
    // a handshake the server rejects would otherwise re-dial on every event.
    if (!force) {
      if (reconnectTimeoutRef.current) return;
      if (Date.now() - lastAttemptAtRef.current < reconnectDelayRef.current) return;
    }

    lastAttemptAtRef.current = Date.now();

    // Only a user-driven attempt supersedes a pending backoff timer.
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const url = import.meta.env.DEV
      ? `ws://localhost:8000/reports/ws/snap-status/${userId}`
      : `wss://${window.location.host}/reports/ws/snap-status/${userId}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = async () => {
      console.log(`[PendingReportSockets] - Conexão WebSocket aberta para ${url}`);
      // A live socket turns off the details query's poll fallback.
      useReportStatusSocketStore.getState().actions.setConnected(true);
      // The ephemeral preview died with the previous socket; re-pull the current
      // partial so persisted sections come back.
      if (hasConnectedRef.current) {
        repullCurrentPartialRef.current();
      }
      hasConnectedRef.current = true;
    };

    ws.onmessage = (evt) => {
      // Back to the base delay only once a connection actually delivered
      // something; a successful open alone proves nothing.
      reconnectDelayRef.current = RECONNECT_BASE_DELAY_MS;
      let payload;
      try {
        payload = JSON.parse(evt.data);
      } catch (error) {
        console.error("Erro ao parsear mensagem WebSocket:", error);
        return;
      }

      const messageType = payload.message_type;

      // Progress frame only: it never carries a terminal report status.
      if (payload.event_type === "snap_progress") {
        const progressReportId: string | undefined = payload.id;
        if (progressReportId) {
          useReportProgressStore.getState().actions.applyProgress(progressReportId, {
            completedSteps: payload.completed_steps,
            totalSteps: payload.total_steps,
            progress: payload.progress,
            finished: payload.milestone === "completed",
          });
        }
        return;
      }

      // Must run before the terminal status branches below — report_partial
      // is transport-only and never itself terminal.
      if (messageType === "report_partial") {
        const frame = parseReportPartialFrame(payload);
        if (!frame || !claimPartialSequenceForRender(frame.id, frame.sequence)) {
          return;
        }

        // Defense-in-depth (bug 2026-08-21 — relatório salvo com dados parciais):
        // na reconexão, o stream durável de Section_Event pode reentregar um
        // snapshot `report_partial` capturado ANTES de o relatório terminar. A
        // autoridade é a cerca terminal server-side (`report_partial_fence`, já
        // aplicada no replay do backend), mas se ESTE cliente já SABE que o
        // relatório é terminal — pelo cache de detalhes ou pela lista carregada
        // — nunca funde/persiste um parcial por cima. Sela a cerca local para que
        // frames seguintes deste id também sejam descartados.
        //
        // Usa o acessor canônico `getReportStatus` (lê `report_status.status_report`
        // via REPORT_CONSTANTS, null-safe p/ objeto malformado) em vez de cavar o
        // shape na mão — não quebra silenciosamente se o nome do campo mudar.
        const cachedDetails = queryClient.getQueryData<ReportData>([
          "reports",
          "details",
          frame.id,
        ]);
        const listedReport = useReportListStore
          .getState()
          .list.find((item) => (item as { user_reports_id?: string })?.user_reports_id === frame.id);
        const { completed, error: errorStatus } = REPORT_CONSTANTS.status;
        const isKnownTerminal = [cachedDetails, listedReport].some((candidate) => {
          if (!candidate) return false;
          const status = getReportStatus(candidate as ReportData);
          return status === completed || status === errorStatus;
        });
        if (isKnownTerminal) {
          markReportTerminal(frame.id);
          return;
        }

        if (frame.progress) {
          useReportProgressStore.getState().actions.applySnapProgress(frame.id, frame.progress);
        }

        // ReportDetailsContainer's existing query-cache sync effect renders
        // this automatically. Merge rules (and why a partial never seeds an
        // absent entry) live in mergeReportDetailsPartialSnapshot.
        queryClient.setQueryData(
          ["reports", "details", frame.id],
          (previous: Record<string, unknown> | undefined) =>
            mergeReportDetailsPartialSnapshot(previous, frame.data),
        );

        savePartialReportMutation.mutate({
          userReportsId: frame.id,
          sequence: frame.sequence,
          snapshot: frame.data,
          progress: frame.progress,
        });
        return;
      }

      // Section events short-circuit here: their "completed"/"error" stage must
      // never be read as a terminal report status. Malformed ones are ignored.
      if (messageType === SECTION_MESSAGE_TYPE) {
        const sectionEvent = parseSectionEvent(payload);
        if (sectionEvent) {
          useSectionStatusStore.getState().actions.applySectionEvent(sectionEvent);
          // Gates internally on `completed`; fire-and-forget so it never blocks
          // status processing.
          void persistCompletedSectionRef.current(sectionEvent);

          // A "Buscar mais dados" result merges into the report on screen, unlike
          // the ephemeral preview above which is status-only. (Dormant: the
          // fetch-more trigger button is currently hidden — #21219 — so this
          // path isn't user-reachable yet, kept for re-enablement.)
          if (sectionEvent.incremental === true) {
            const currentMetadata = useReportDetailStore.getState().data.metadata;
            const isCurrentReport = currentMetadata?.user_reports_id === sectionEvent.user_reports_id;
            console.info(
              `[PendingReportSockets] Incremental section event received section_id=${sectionEvent.section_id} ` +
                `event_report_id=${sectionEvent.user_reports_id} current_report_id=${currentMetadata?.user_reports_id} ` +
                `isCurrentReport=${isCurrentReport}`,
            );
            if (isCurrentReport) {
              const sectionPayload = extractIncrementalSectionPayload(sectionEvent);
              const rsId = extractRsIdFromSectionId(sectionEvent.section_id);
              console.info(
                `[PendingReportSockets] Merge inputs: hasPayload=${!!sectionPayload} ` +
                  `title=${sectionPayload?.title} subsection=${sectionPayload?.subsection} ` +
                  `dataLength=${sectionPayload?.data?.length} rsId=${rsId}`,
              );
              if (sectionPayload && rsId) {
                useReportDetailStore
                  .getState()
                  .actions.mergeIncrementalSectionData(
                    sectionPayload.title,
                    sectionPayload.subsection,
                    sectionPayload.data,
                    resolveIdentityLabels(rsId),
                  );
                console.info(
                  `[PendingReportSockets] mergeIncrementalSectionData called for section_id=${sectionEvent.section_id}`,
                );
              }
            }
          }
        }
        return;
      }

      const status = payload[REPORT_CONSTANTS.report_status_message.status];
      const { completed, error } = REPORT_CONSTANTS.status;

      if (messageType === "summary") {
        const reportId = payload.id;
        const eventSummaryId: string | undefined = payload.summary_id ?? payload.result?.summary_id;

        // Only the report on screen gets its store updated.
        const currentMetadata = useReportDetailStore.getState().data.metadata;
        const currentReportId = currentMetadata?.user_reports_id;
        const isCurrentReport = currentReportId === reportId;

        // Drops events from a superseded generation. The cache holds a
        // SummaryOutputDTO, so the id comes from getSummaryId().
        const cachedSummary = queryClient.getQueryData<any>(["reports", "summary", reportId]);
        const cachedSummaryId: string | undefined = cachedSummary?.getSummaryId?.() || undefined;
        if (eventSummaryId && cachedSummaryId && eventSummaryId !== cachedSummaryId) {
          console.warn(
            `[PendingReportSockets] Ignorando evento de geração obsoleta: ` +
            `eventSummaryId=${eventSummaryId}, cachedSummaryId=${cachedSummaryId}, reportId=${reportId}`
          );
          return;
        }

        // Keeps the list store and its query cache in sync.
        const updateReportListCache = (hasSummary: boolean, summaryGenerating: boolean) => {
          const fields = { has_summary: hasSummary, summary_generating: summaryGenerating } as any;
          updateItemById(reportId, fields);
          queryClient.setQueriesData<any>(
            { queryKey: ["reports", "list"], exact: false },
            (oldData: any) => {
              if (!oldData?.data) return oldData;
              return {
                ...oldData,
                data: oldData.data.map((item: any) => {
                  if (item?.user_reports_id === reportId) {
                    return { ...item, ...fields };
                  }
                  return item;
                }),
              };
            }
          );
        };

        // Intermediate stages carry no terminal status.
        if (status !== completed && status !== error) {
          return;
        }

        if (status === error) {
          // Service-provided message, used by the toast and the indicator.
          const apiErrorMessage = normalizeErrorMessage(payload.result?.message);

          // Task 5.17: terminal de erro do sumário IA (assíncrono via este
          // mesmo socket) — consome a marca de início por `reportId`.
          emitEvent({
            name: "ai_summary",
            phase: "error",
            durationMs: consumeSummaryDuration(reportId) ?? undefined,
          });

          if (isCurrentReport) {
            setSummaryStatus("error");
            // Null keeps the indicator on its fallback message.
            setSummaryErrorMessage(apiErrorMessage ?? null);
          }
          // Always cleared, even off-screen, so the error is not suppressed later.
          setIsGeneratingSummary(reportId, false);

          // Generation failed — no valid summary exists, so the badge must not show.
          updateReportListCache(false, false);

          const description = apiErrorMessage || "Ocorreu um erro ao tentar gerar o sumário. Tente novamente.";
          toast.error("Erro ao gerar sumário", { description });
          withPasswordCheck(() => {
            invalidateSummary(reportId);
          });
          return;
        }

        if (status === completed) {
          const summaryData = payload.result?.data;
          if (summaryData) {
            const summaryRegistry = issuedSummarySavesRef.current;

            // Ignores re-sends of the same `completed` while the save is in
            // flight or already done.
            if (!shouldIssueSave(eventSummaryId, summaryRegistry)) {
              return;
            }

            // Task 5.17: terminal de sucesso do sumário IA — a geração em si
            // terminou aqui; a persistência local (abaixo) é uma etapa
            // separada, não recontada nesta medição.
            emitEvent({
              name: "ai_summary",
              phase: "success",
              durationMs: consumeSummaryDuration(reportId) ?? undefined,
            });

            if (isCurrentReport) {
              setSummaryStatus("pending");
            }

            // List cache: summary ready.
            updateReportListCache(true, false);

            const updatePayload = {
              summary_status: "success" as const,
              data: summaryData,
            };

            // Marked before the mutation so in-flight re-sends are blocked.
            if (eventSummaryId) {
              markSaveIssued(eventSummaryId, summaryRegistry);
            }

            updateResumeMutation.mutate(
              {
                reportId,
                summaryPayload: updatePayload as any,
                report: queryClient.getQueryData<any>(["reports", "details", reportId]),
              },
              {
                onSuccess: () => {
                  // Only the report on screen gets its store updated.
                  if (isCurrentReport) {
                    setSummaryData(summaryData);
                    setSummaryStatus("success");
                  }
                  setIsGeneratingSummary(reportId, false);
                  toast.success("Sumário Executivo gerado com sucesso!", {
                    description: "Seu sumário está pronto para ser visualizado.",
                  });
                  withPasswordCheck(() => {
                    invalidateSummary(reportId);
                  });
                },
                onError: () => {
                  // Released so a legitimate retry can save again.
                  if (eventSummaryId) {
                    releaseSave(eventSummaryId, summaryRegistry);
                  }
                  if (isCurrentReport) {
                    setSummaryStatus("error");
                  }
                  setIsGeneratingSummary(reportId, false);
                  toast.error("Erro ao salvar sumário", {
                    description: "Ocorreu um erro ao tentar salvar o sumário.",
                  });
                },
              }
            );
          }
        }
        return;
      }

      if (status === error) {
        // Terminal error: drop any streaming progress/preview so the card/details
        // leave their in-flight state and show the error affordance.
        const erroredReportId: string | undefined = payload.id ?? payload.result?.data?.user_reports_id;
        if (erroredReportId) {
          // Fences any late/in-flight partial for this report before it can
          // overwrite the terminal error.
          markReportTerminal(erroredReportId);
          useReportProgressStore.getState().actions.clearProgress(erroredReportId);
          useSectionStatusStore.getState().actions.clearPreviewForReport(erroredReportId);
          // Revalidate now so an open details screen shows the error immediately.
          invalidateReportDetails(erroredReportId);
        }
        const currentFolderId = window.location.pathname.startsWith('/pasta/')
          ? window.location.pathname.split('/pasta/')[1]
          : null;
        toast.error("Erro ao gerar relatório", {
          description: "Ocorreu um erro ao tentar gerar o relatório. Tente novamente.",
        });
        withPasswordCheck(() => {
          invalidateCurrentFolderNoFilters(currentFolderId);
        });
      }

      if (status === completed) {
        const newReport = payload.result?.data;
        if (newReport) {
          const reportSaveId: string | undefined = newReport.user_reports_id;
          // Fences any late/in-flight partial for this report before it can
          // overwrite the terminal success.
          if (reportSaveId) {
            markReportTerminal(reportSaveId);
          }
          const registry = issuedReportSavesRef.current;

          // Ignores re-sends of the same `completed` while the save is in flight
          // or already done.
          if (!shouldIssueSave(reportSaveId, registry)) {
            return;
          }

          // Marked before the mutation so in-flight re-sends are blocked.
          if (reportSaveId) {
            markSaveIssued(reportSaveId, registry);
          }

          // Task 5.15: fecha a latência percebida create→ready no frame
          // terminal `completed`, reutilizando o mesmo dedup por id acima —
          // descarta medições sem start recorded/já medidas/reenviadas.
          recordCreateCompleted(reportSaveId);

          addNewReportMutation.mutate(newReport, {
            onSuccess: (data) => {
              // Deleted mid-processing: the backend answers 200 `skipped`.
              if ((data as { skipped?: boolean } | undefined)?.skipped) {
                return;
              }

              // Scoped to this report so another one's live preview survives.
              useSectionStatusStore.getState().actions.clearPreviewForReport(reportSaveId);
              if (reportSaveId) {
                // Progress is done once the final report is persisted.
                useReportProgressStore.getState().actions.clearProgress(reportSaveId);

                // Swaps the pending view for the final one; scoped by id and cheap.
                invalidateReportDetails(reportSaveId);
              }

              const currentFolderId = window.location.pathname.startsWith('/pasta/')
                ? window.location.pathname.split('/pasta/')[1]
                : null;
              toast.success("Relatório gerado com sucesso!", {
                description: "Seu relatório está pronto para ser acessado.",
              });
              withPasswordCheck(() => {
                invalidateUserData();
                invalidateCurrentFolderNoFilters(currentFolderId);
              });
            },
            onError: (error: unknown) => {
              // Released so a legitimate retry can save again.
              if (reportSaveId) {
                releaseSave(reportSaveId, registry);
              }
              return createErrorHandler("Erro ao tentar salvar o novo relatório", "Erro ao salvar")(
                error as any,
              );
            },
          });
        }
      }
    };
    ws.onerror = (e) => {
      console.log("Erro na conexão WebSocket:", e)
    };
    ws.onclose = (e) => {
      console.log(`[PendingReportSockets] WS fechado code=${e?.code} reason=${e?.reason || "-"}`);
      useReportStatusSocketStore.getState().actions.setConnected(false);

      // 1008 é recusa de política (token ausente/inválido/sub divergente): tentar
      // de novo não muda nada até a sessão ser renovada.
      if (e?.code === WS_POLICY_VIOLATION) {
        return;
      }

      // Only re-dial when something that depends on this socket is in flight.
      // Defensive reads: a throw here would kill reconnection.
      const shouldReconnect = shouldReconnectStatusSocket({
        isAuthorized: Boolean(userIdRef.current && isUserAuthorizedRef.current),
        hasStreamingProgress:
          Object.keys(useReportProgressStore.getState()?.progress ?? {}).length > 0,
        hasSectionPreview: (useSectionStatusStore.getState()?.order?.length ?? 0) > 0,
        isGeneratingSummary:
          (useReportDetailStore.getState()?.ui?.generatingSummaryReportIds?.length ?? 0) > 0,
        hasServerPendingWork: hasPendingWorkInSnapshots(cachedReportsRef.current()),
        hasExplicitWork: hasExplicitStatusSocketWork(),
      });

      if (!shouldReconnect) {
        console.log("[PendingReportSockets] Sem trabalho pendente — não reconectando.");
        return;
      }

      if (reconnectTimeoutRef.current) {
        return; // already scheduled
      }
      const delay = reconnectDelayRef.current;
      console.log(`[PendingReportSockets] Reconectando em ${delay}ms...`);
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        reconnectDelayRef.current = Math.min(delay * 2, RECONNECT_MAX_DELAY_MS);
        connect(true);
      }, delay);
    };
    // Dependências estáveis: `connect` só muda de identidade quando o
    // `user_id` ou o estado de autorização mudam de fato — não em re-renders
    // não relacionados (ex.: nova identidade do objeto `userData`). Isso evita
    // o ciclo tear-down/reopen do socket a cada render.
  }, [userId, isUserAuthorized]);

  // The frontend only closes on unmount or lost auth — never on a re-render or
  // a route change.
  useEffect(() => {
    connect(true);
  }, [connect]);

  useEffect(() => {
    if (userId && isUserAuthorized) return;
    useReportStatusSocketStore.getState().actions.setConnected(false);
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    wsRef.current?.close();
    wsRef.current = null;
  }, [userId, isUserAuthorized]);

  useEffect(() => {
    return () => {
      useReportStatusSocketStore.getState().actions.setConnected(false);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      wsRef.current?.close();
    };
  }, []);

  // Work can appear later (another tab, another device, a list refetch), so any
  // cache update that reveals it reopens the socket.
  useEffect(() => {
    if (typeof queryClient?.getQueryCache !== "function") return;
    const cache = queryClient.getQueryCache();
    if (typeof cache?.subscribe !== "function") return;

    return cache.subscribe(() => {
      if (!userId || !isUserAuthorized) return;
      const socket = wsRef.current;
      if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
        return;
      }
      if (hasPendingWorkInSnapshots(cachedReportsRef.current())) {
        connect();
      }
    });
  }, [connect, queryClient, userId, isUserAuthorized]);

  // Ações cujo resultado só chega por este socket precisam dele vivo.
  useEffect(() => {
    const handler = () => {
      markStatusSocketWork();
      connect(true);
    };
    window.addEventListener('snap:report-created', handler);
    window.addEventListener('snap:socket-needed', handler);
    return () => {
      window.removeEventListener('snap:report-created', handler);
      window.removeEventListener('snap:socket-needed', handler);
    };
  }, [connect]);

  return null;
}